import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, getValidToken } from '../utils/authUtils';

const AdminRecruiterPipeline = () => {
  const navigate = useNavigate();

  // Data state
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 300;

  // Filter state
  const [cycleYear, setCycleYear] = useState(null);
  const [availableCycles, setAvailableCycles] = useState([]);
  const [currentCycleYear, setCurrentCycleYear] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [pipelineStatus, setPipelineStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Summary stats
  const [summary, setSummary] = useState({
    total_in_pipeline: 0,
    interview_only: 0,
    direct_offer: 0,
    offer_pending: 0,
    accepted: 0,
    declined: 0
  });

  // Sorting state
  const [sortBy, setSortBy] = useState('organization');
  const [sortOrder, setSortOrder] = useState('asc');

  // Export state
  const [exporting, setExporting] = useState(false);

  // Unique students view state
  const [uniqueStudents, setUniqueStudents] = useState(false);

  // Check authentication
  useEffect(() => {
    const token = getValidToken();
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  // Fetch pipeline data
  const fetchPipeline = useCallback(async () => {
    if (!cycleYear) return;

    setLoading(true);
    setError(null);

    try {
      const token = getValidToken();
      const params = new URLSearchParams({
        cycle_year: cycleYear,
        page: currentPage,
        per_page: perPage,
        sort_by: sortBy,
        sort_order: sortOrder,
        pipeline_status: pipelineStatus,
        unique_students: uniqueStudents
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (selectedOrganization) {
        params.append('organization', selectedOrganization);
      }

      const response = await axios.get(
        `${API_URL}/api/admin/recruiter-pipeline?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setPipeline(response.data.pipeline);
        setTotalPages(response.data.pagination.total_pages);
        setTotalRecords(response.data.pagination.total_records);
        setCurrentPage(response.data.pagination.page);
        setOrganizations(response.data.filters.organizations);
        setSummary(response.data.summary);

        // Set available cycles if not already set
        if (response.data.filters.cycle_years && availableCycles.length === 0) {
          setAvailableCycles(response.data.filters.cycle_years);
          const current = response.data.filters.cycle_years.find(c => c.is_current);
          if (current) {
            setCurrentCycleYear(current.year);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching pipeline:', err);
      setError('Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  }, [cycleYear, currentPage, sortBy, sortOrder, searchTerm, selectedOrganization, pipelineStatus, uniqueStudents, availableCycles.length]);

  // Initial load - get cycle years first
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = getValidToken();
        // Use the main endpoint to get cycle years from filters
        const response = await axios.get(
          `${API_URL}/api/admin/recruiter-pipeline?page=1&per_page=1`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success && response.data.filters.cycle_years) {
          setAvailableCycles(response.data.filters.cycle_years);
          const current = response.data.filters.cycle_years.find(c => c.is_current);
          if (current) {
            setCurrentCycleYear(current.year);
            setCycleYear(current.year);
          } else if (response.data.filters.cycle_years.length > 0) {
            setCycleYear(response.data.filters.cycle_years[0].year);
          }
        }
      } catch (err) {
        console.error('Error fetching initial data:', err);
        // Fallback to current year
        const currentYear = new Date().getFullYear();
        setCycleYear(currentYear);
        setAvailableCycles([{ year: currentYear, is_current: true, student_count: 0 }]);
      }
    };
    fetchInitialData();
  }, []);

  // Fetch pipeline when dependencies change
  useEffect(() => {
    if (cycleYear) {
      fetchPipeline();
    }
  }, [fetchPipeline, cycleYear]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortBy !== column) {
      return <span className="text-gray-300 ml-1">↕</span>;
    }
    return sortOrder === 'asc'
      ? <span className="text-blue-600 ml-1">↑</span>
      : <span className="text-blue-600 ml-1">↓</span>;
  };

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    try {
      const token = getValidToken();
      const params = new URLSearchParams({
        cycle_year: cycleYear,
        sort_by: sortBy,
        sort_order: sortOrder,
        pipeline_status: pipelineStatus,
        unique_students: uniqueStudents
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (selectedOrganization) {
        params.append('organization', selectedOrganization);
      }

      const response = await axios.get(
        `${API_URL}/api/admin/recruiter-pipeline/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const statusSuffix = pipelineStatus !== 'all' ? `_${pipelineStatus}` : '';
      const uniqueSuffix = uniqueStudents ? '_unique_students' : '';
      link.setAttribute('download', `recruiter_pipeline_cycle${cycleYear}${statusSuffix}${uniqueSuffix}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting:', err);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Handle filter changes
  const handleCycleChange = (e) => {
    setCycleYear(parseInt(e.target.value));
    setCurrentPage(1);
    setSelectedOrganization('');
  };

  const handleOrganizationChange = (e) => {
    setSelectedOrganization(e.target.value);
    setCurrentPage(1);
  };

  const handlePipelineStatusChange = (e) => {
    setPipelineStatus(e.target.value);
    setCurrentPage(1);
  };

  const handleUniqueStudentsToggle = () => {
    setUniqueStudents(!uniqueStudents);
    setCurrentPage(1);
  };

  // Get status badge
  const getStatusBadge = (item) => {
    if (item.offer_status === 'accepted') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Accepted</span>;
    } else if (item.offer_status === 'declined') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Declined</span>;
    } else if (item.offer_status === 'pending') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">Offer Pending</span>;
    } else if (!item.interview_date && item.offer_date) {
      // Direct offer - has offer but no interview date
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">Direct Offer*</span>;
    } else {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Interview**</span>;
    }
  };

  // Pagination component
  const Pagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center gap-2 my-4">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ◀◀
        </button>
        <button
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ◀ Prev
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => setCurrentPage(1)}
              className="px-3 py-1 rounded border bg-white hover:bg-gray-100"
            >
              1
            </button>
            {startPage > 2 && <span className="px-2">...</span>}
          </>
        )}

        {pages.map(page => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            className={`px-3 py-1 rounded border ${
              currentPage === page
                ? 'bg-blue-600 text-white'
                : 'bg-white hover:bg-gray-100'
            }`}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-2">...</span>}
            <button
              onClick={() => setCurrentPage(totalPages)}
              className="px-3 py-1 rounded border bg-white hover:bg-gray-100"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next ▶
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ▶▶
        </button>
      </div>
    );
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Internship Pipeline</h1>
              <p className="text-sm text-gray-600 mt-1">Students in interview/offer pipeline by company</p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border rounded hover:bg-gray-50"
            >
              ← Back to Admin
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-gray-400">
            <div className="text-2xl font-bold text-gray-900">{summary.total_in_pipeline}</div>
            <div className="text-sm text-gray-600">Total in Pipeline</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-400">
            <div className="text-2xl font-bold text-blue-700">{summary.interview_only}</div>
            <div className="text-sm text-gray-600">Interview Only**</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-400">
            <div className="text-2xl font-bold text-purple-700">{summary.direct_offer || 0}</div>
            <div className="text-sm text-gray-600">Direct Offer*</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-400">
            <div className="text-2xl font-bold text-orange-700">{summary.offer_pending}</div>
            <div className="text-sm text-gray-600">Offer Pending</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-400">
            <div className="text-2xl font-bold text-green-700">{summary.accepted}</div>
            <div className="text-sm text-gray-600">Accepted</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-400">
            <div className="text-2xl font-bold text-red-700">{summary.declined}</div>
            <div className="text-sm text-gray-600">Declined</div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Cycle Year Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Year</label>
              <select
                value={cycleYear || ''}
                onChange={handleCycleChange}
                className="px-3 py-2 border rounded-md min-w-[180px] bg-white"
              >
                {availableCycles.map(cycle => (
                  <option key={cycle.year} value={cycle.year}>
                    {cycle.year} {cycle.is_current ? '(Current)' : ''} - {cycle.student_count} students
                  </option>
                ))}
              </select>
            </div>

            {/* Organization Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
              <select
                value={selectedOrganization}
                onChange={handleOrganizationChange}
                className="px-3 py-2 border rounded-md min-w-[200px] bg-white"
              >
                <option value="">All Organizations</option>
                {organizations.map(org => (
                  <option key={org} value={org}>{org}</option>
                ))}
              </select>
            </div>

            {/* Pipeline Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Status</label>
              <select
                value={pipelineStatus}
                onChange={handlePipelineStatusChange}
                className="px-3 py-2 border rounded-md min-w-[180px] bg-white"
              >
                <option value="all">All</option>
                <option value="interview_only">Interview Only**</option>
                <option value="direct_offer">Direct Offer*</option>
                <option value="offer_pending">Offer Pending</option>
                <option value="accepted">Accepted</option>
                <option value="declined">Declined</option>
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search student name or email..."
                className="px-3 py-2 border rounded-md min-w-[280px]"
              />
            </div>

            {/* Unique Students Toggle */}
            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={uniqueStudents}
                    onChange={handleUniqueStudentsToggle}
                    className="sr-only"
                  />
                  <div className={`w-14 h-7 rounded-full shadow-inner transition-colors ${uniqueStudents ? 'bg-indigo-600' : 'bg-gray-300'}`}></div>
                  <div className={`absolute w-5 h-5 bg-white rounded-full shadow top-1 transition-transform ${uniqueStudents ? 'translate-x-8' : 'translate-x-1'}`}></div>
                </div>
                <span className="ml-3 text-sm font-medium text-gray-700">
                  {uniqueStudents ? 'Unique Students' : 'All Interactions'}
                </span>
              </label>
            </div>

            {/* Export Button */}
            <div className="ml-auto">
              <button
                onClick={handleExport}
                disabled={exporting || loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {exporting ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Exporting...
                  </>
                ) : (
                  <>
                    📥 Export CSV
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Pagination Info Banner */}
        <div className={`border rounded-lg p-3 mb-4 ${uniqueStudents ? 'bg-indigo-50 border-indigo-200' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={uniqueStudents ? 'text-indigo-600 text-lg' : 'text-blue-600 text-lg'}>ℹ️</span>
              <span className={uniqueStudents ? 'text-indigo-800 font-medium' : 'text-blue-800 font-medium'}>
                Displaying {perPage} records per page | Page {currentPage} of {totalPages} | {totalRecords.toLocaleString()} {uniqueStudents ? 'unique students' : 'student-company interactions'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {uniqueStudents && (
                <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-medium">
                  Unique Students Mode
                </span>
              )}
              {cycleYear === currentCycleYear && (
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  Current Cycle
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Top Pagination */}
        {totalPages > 1 && <Pagination />}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Data Table - Fixed height with sticky columns */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-auto max-h-[600px]" style={{ position: 'relative' }}>
            <table className="min-w-full divide-y divide-gray-200 border-collapse">
              <thead className="bg-gray-50 sticky top-0 z-30">
                <tr>
                  {/* Sticky column: Organization */}
                  <th
                    onClick={() => handleSort('organization')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 sticky left-0 z-20 border-r-2 border-gray-300"
                    style={{ minWidth: '150px' }}
                  >
                    Organization {renderSortIcon('organization')}
                  </th>
                  {/* Non-sticky columns */}
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    First Name
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Last Name
                  </th>
                  <th
                    onClick={() => handleSort('student_name')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Full Name {renderSortIcon('student_name')}
                  </th>
                  <th
                    onClick={() => handleSort('recruiter_name')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Recruiter {renderSortIcon('recruiter_name')}
                  </th>
                  <th
                    onClick={() => handleSort('student_id')}
                    className="px-4 py-3 text-center text-xs font-medium text-cyan-700 uppercase tracking-wider cursor-pointer hover:bg-cyan-100 bg-cyan-50 border-x-2 border-cyan-300"
                  >
                    ID {renderSortIcon('student_id')}
                  </th>
                  <th
                    onClick={() => handleSort('student_email')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    University Email {renderSortIcon('student_email')}
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Personal Email
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Cell Phone
                  </th>
                  <th
                    onClick={() => handleSort('university')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    University {renderSortIcon('university')}
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Class Year
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Major
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    GPA
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Citizenship Status
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Gender
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Race
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Ethnicity
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Expected Graduation Date
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Application Date
                  </th>
                  <th
                    onClick={() => handleSort('shortlisted_date')}
                    className="px-4 py-3 text-center text-xs font-medium text-indigo-600 uppercase tracking-wider cursor-pointer hover:bg-indigo-50 bg-indigo-50"
                  >
                    Shortlisted {renderSortIcon('shortlisted_date')}
                  </th>
                  <th
                    onClick={() => handleSort('interview_date')}
                    className="px-4 py-3 text-center text-xs font-medium text-purple-600 uppercase tracking-wider cursor-pointer hover:bg-purple-50 bg-purple-50"
                  >
                    Interview Date {renderSortIcon('interview_date')}
                  </th>
                  <th
                    onClick={() => handleSort('offer_status')}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Status {renderSortIcon('offer_status')}
                  </th>
                  <th
                    onClick={() => handleSort('offer_date')}
                    className="px-4 py-3 text-center text-xs font-medium text-orange-600 uppercase tracking-wider cursor-pointer hover:bg-orange-50 bg-orange-50"
                  >
                    Offer Date {renderSortIcon('offer_date')}
                  </th>
                  <th
                    onClick={() => handleSort('start_date')}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Start Date {renderSortIcon('start_date')}
                  </th>
                  <th
                    onClick={() => handleSort('internship_length')}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Length (wks) {renderSortIcon('internship_length')}
                  </th>
                  {uniqueStudents && (
                    <th
                      className="px-4 py-3 text-center text-xs font-medium text-indigo-700 uppercase tracking-wider bg-indigo-50 border-x-2 border-indigo-300"
                    >
                      # Companies
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={uniqueStudents ? "26" : "25"} className="px-4 py-8 text-center text-gray-500">
                      <div className="flex items-center justify-center gap-2">
                        <span className="animate-spin text-2xl">⏳</span>
                        Loading pipeline data...
                      </div>
                    </td>
                  </tr>
                ) : pipeline.length === 0 ? (
                  <tr>
                    <td colSpan={uniqueStudents ? "26" : "25"} className="px-4 py-8 text-center text-gray-500">
                      No students found in pipeline for the selected filters.
                    </td>
                  </tr>
                ) : (
                  pipeline.map((item, index) => (
                    <tr key={`${item.student_id}-${item.recruiter_id}-${index}`} className="hover:bg-gray-50 group">
                      {/* Sticky column: Organization */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 bg-white group-hover:bg-gray-50 sticky left-0 z-10 border-r-2 border-gray-200" style={{ minWidth: '150px' }}>
                        <span className="truncate block max-w-[140px]" title={item.organization}>
                          {item.organization}
                        </span>
                      </td>
                      {/* Non-sticky columns */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {item.first_name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {item.last_name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {item.student_name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.recruiter_name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-cyan-50 border-x-2 border-cyan-200">
                        <span className="font-medium text-cyan-700">{item.student_id}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.student_email ? (
                          <a
                            href={`mailto:${item.student_email}`}
                            className="text-blue-600 hover:underline"
                          >
                            {item.student_email}
                          </a>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.personal_email ? (
                          <a
                            href={`mailto:${item.personal_email}`}
                            className="text-blue-600 hover:underline"
                          >
                            {item.personal_email}
                          </a>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.cell_phone || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        <span className="truncate block max-w-[150px]" title={item.university}>
                          {item.university || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.class_year || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.major || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600">
                        {item.gpa || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.citizenship_status || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.gender || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.race || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.ethnicity || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600">
                        {formatDate(item.expected_grad_date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600">
                        {formatDate(item.application_date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-indigo-50">
                        <span className={`font-medium ${item.shortlisted_date ? 'text-indigo-700' : 'text-gray-400'}`}>
                          {formatDate(item.shortlisted_date)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-purple-50">
                        <span className="font-medium text-purple-700">
                          {formatDate(item.interview_date)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                        {getStatusBadge(item)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-orange-50">
                        <span className={`font-medium ${item.offer_date ? 'text-orange-700' : 'text-gray-400'}`}>
                          {formatDate(item.offer_date)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600">
                        {formatDate(item.start_date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600">
                        {item.internship_length || '-'}
                      </td>
                      {uniqueStudents && (
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center font-medium text-indigo-700 bg-indigo-50 border-x-2 border-indigo-100">
                          {item.company_count || 1}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footnotes */}
        <div className="mt-3 px-2 text-xs text-gray-500 space-y-1">
          <p>* <span className="italic">Direct Offer: Student received an offer but was never added to the interview queue in NACME Searchlight. This typically occurs when recruiters conduct interviews through their own ATS system.</span></p>
          <p>** <span className="italic">Interview Only: Student was added to the interview queue but has not yet received an offer.</span></p>
        </div>

        {/* Unique Students Mode Notation */}
        {uniqueStudents && (
          <div className="mt-4 px-3 py-3 bg-indigo-50 border border-indigo-200 rounded-lg">
            <p className="text-sm font-medium text-indigo-800 mb-2">📋 Unique Students View - Furthest Along Company</p>
            <p className="text-xs text-indigo-700 mb-2">
              When a student appears in multiple companies' pipelines, this view shows only the company where the student is <strong>furthest along</strong> in the hiring process.
            </p>
            <p className="text-xs text-indigo-600 mb-1"><strong>Status Priority (highest to lowest):</strong></p>
            <ol className="text-xs text-indigo-600 list-decimal list-inside ml-2 space-y-0.5">
              <li>Accepted</li>
              <li>Declined</li>
              <li>Offer Pending</li>
              <li>Interview Only</li>
              <li>Direct Offer</li>
              <li>Shortlisted</li>
            </ol>
            <p className="text-xs text-indigo-600 mt-2">
              <strong>Tiebreaker:</strong> If a student is at the same stage with multiple companies, the company with the most recent activity is shown.
            </p>
            <p className="text-xs text-indigo-600 mt-1">
              <strong># Companies:</strong> The highlighted column shows the total number of companies where each student appears in the pipeline.
            </p>
          </div>
        )}

        {/* Sorting Guidance */}
        <div className="mt-4 px-3 py-3 bg-cyan-50 border border-cyan-200 rounded-lg">
          <p className="text-sm font-medium text-cyan-800 mb-1">📊 Sorting Guide:</p>
          <ul className="text-xs text-cyan-700 space-y-1 ml-4 list-disc">
            <li><span className="font-medium">Sort by ID</span> to view each student's status across all companies they're engaged with.</li>
            <li><span className="font-medium">Sort by Organization</span> to view each company's status with all students in their pipeline.</li>
          </ul>
        </div>

        {/* Bottom Pagination */}
        {totalPages > 1 && <Pagination />}
      </main>
    </div>
  );
};

export default AdminRecruiterPipeline;
